// THE REEL — admin-only album. Tick shows in THE VÖLT → they queue here → audition track by track straight from archive.org →
// ♥ puts the song in THE VÖLT and on air in the Hoard player; ✕ ("kwiki") passes and moves on. Every play lands in your Listened list.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Heart, X, Play, Pause, SkipBack, SkipForward, ListMusic, ChevronDown, ChevronUp, Loader2, Trash2, Radio } from "lucide-react";
import { API } from "../../App";
import { useIsVoltAdmin } from "./VoltPicks";
import { logListen } from "./ListenLog";
import { usePlayer } from "../../player/PlayerContext";
import { useKrakon } from "../krakon/KrakonDock";

const ax = { withCredentials: true };
const POS_KEY = "wall_reel_pos";
let _reel = null;
const _subs = new Set();
const publish = (d) => { _reel = d; _subs.forEach((f) => f(d)); };
const mmss = (s) => (Number.isFinite(s) && s > 0 ? `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}` : "0:00");

export const useReel = () => {
  const admin = useIsVoltAdmin();
  const [reel, set] = useState(_reel || { shows: [], queue: 0 });
  useEffect(() => {
    if (!admin) return undefined;
    _subs.add(set);
    if (_reel == null) axios.get(`${API}/admin/volt/reel`, ax).then((r) => publish(r.data)).catch(() => {});
    return () => _subs.delete(set);
  }, [admin]);
  const refresh = useCallback(() => axios.get(`${API}/admin/volt/reel`, ax).then((r) => publish(r.data)).catch(() => {}), []);
  const has = useCallback((show_id) => reel.shows.some((s) => s.show_id === show_id), [reel]);
  const add = useCallback(async (show_id) => { try { const r = await axios.post(`${API}/admin/volt/reel`, { show_id }, ax); publish(r.data); toast.success("On the Reel — open the admin player to audition it"); } catch (e) { toast.error(e.response?.data?.detail || "Couldn't add"); } }, []);
  const remove = useCallback(async (show_id) => { try { const r = await axios.delete(`${API}/admin/volt/reel`, { ...ax, params: show_id ? { show_id } : {} }); publish(r.data); } catch { toast.error("Couldn't remove"); } }, []);
  const vote = useCallback(async (show_id, file, v) => { try { await axios.post(`${API}/admin/volt/reel/vote`, { show_id, file, vote: v }, ax); refresh(); return true; } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save that"); return false; } }, [refresh]);
  return { admin, reel, has, add, remove, vote, refresh, toggle: (show_id) => (has(show_id) ? remove(show_id) : add(show_id)) };
};

// Checkbox on a show row: on the Reel or not.
export const ReelCheckbox = ({ show, dark }) => {
  const r = useReel();
  if (!r.admin) return null;
  const on = r.has(show.id);
  return (
    <label className={`shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-black cursor-pointer transition-colors ${on ? "bg-[#F5D06F] text-[#120A24]" : dark ? "bg-white/10 hover:bg-white/20" : "bg-[#F3F1EC] hover:bg-[#E2E0DC]"}`} title={on ? "On your Reel — untick to remove" : "Add this show to your Reel (admin player)"}>
      <input type="checkbox" checked={on} onChange={() => r.toggle(show.id)} className="accent-[#F5D06F] h-3 w-3" data-testid={`reel-check-${show.id}`} /> reel
    </label>
  );
};

// Flat list of every track on the Reel, in show order.
const flatten = (shows) => shows.flatMap((s) => s.tracks.map((t, i) => ({ ...t, idx: i, show_id: s.show_id, artist: s.artist, show_title: s.title, date: s.date, venue: s.venue, vote: s.votes?.[t.file], cached: s.cached?.[t.file] })));

export const AuditionPlayer = () => {
  const r = useReel();
  const llama = usePlayer();
  const [narrow, setNarrow] = useState(() => window.innerWidth < 640);
  useEffect(() => { const f = () => setNarrow(window.innerWidth < 640); window.addEventListener("resize", f); return () => window.removeEventListener("resize", f); }, []);
  // Phones: while the llama player is open, the Reel tucks into a small pill above it instead of covering it.
  const squeeze = narrow && !!llama?.current && !llama.minimized;
  const kr = useKrakon();
  const krakensOpen = !!kr?.open && !kr.minimized;
  const tracks = useMemo(() => flatten(r.reel.shows || []), [r.reel]);
  const [pos, setPos] = useState(() => { try { return JSON.parse(localStorage.getItem(POS_KEY) || "null"); } catch { return null; } });
  const [open, setOpen] = useState(true);
  const [showQueue, setShowQueue] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [dur, setDur] = useState(0);
  const audio = useRef(null);
  const cur = useMemo(() => {
    if (!tracks.length) return null;
    const i = pos ? tracks.findIndex((t) => t.show_id === pos.show_id && t.file === pos.file) : -1;
    return tracks[i >= 0 ? i : 0];
  }, [tracks, pos]);
  const curIndex = cur ? tracks.indexOf(cur) : -1;
  const nextUnheard = useCallback((from) => { for (let i = from + 1; i < tracks.length; i++) if (!tracks[i].vote) return tracks[i]; return tracks[from + 1] || null; }, [tracks]);

  const load = useCallback((t, autoplay = true) => {
    if (!t) return;
    setPos({ show_id: t.show_id, file: t.file });
    localStorage.setItem(POS_KEY, JSON.stringify({ show_id: t.show_id, file: t.file }));
    const a = audio.current;
    if (!a) return;
    a.src = `https://archive.org/download/${t.show_id}/${encodeURIComponent(t.file)}`;
    a.currentTime = 0;
    if (autoplay) { window.dispatchEvent(new CustomEvent("wall:pause-music")); a.play().then(() => { setPlaying(true); logListen({ kind: "played", show_id: t.show_id, artist: t.artist, album: t.show_title, title: t.title, date: t.date, venue: t.venue }); }).catch(() => setPlaying(false)); }
  }, []);
  const toggle = () => {
    const a = audio.current;
    if (!a || !cur) return;
    if (playing) { a.pause(); setPlaying(false); return; }
    if (!a.src) { load(cur); return; }
    window.dispatchEvent(new CustomEvent("wall:pause-music"));
    a.play().then(() => setPlaying(true)).catch(() => {});
  };
  const step = (d) => { const t = tracks[curIndex + d]; if (t) load(t, playing || true); };
  const like = async () => {
    if (!cur) return;
    const ok = await r.vote(cur.show_id, cur.file, "like");
    if (!ok) return;
    toast.success(`♥ ${cur.title} — heading into THE VÖLT, on air in a minute`);
    logListen({ kind: "picked", show_id: cur.show_id, artist: cur.artist, album: cur.show_title, title: cur.title, date: cur.date, venue: cur.venue });
    load(nextUnheard(curIndex));
  };
  const kwiki = async () => {
    if (!cur) return;
    const ok = await r.vote(cur.show_id, cur.file, "pass");
    if (!ok) return;
    logListen({ kind: "passed", show_id: cur.show_id, artist: cur.artist, album: cur.show_title, title: cur.title, date: cur.date, venue: cur.venue });
    load(nextUnheard(curIndex));
  };
  // Pause when the main WALL player starts; refresh import status while something is in flight.
  useEffect(() => { const h = () => { audio.current?.pause(); setPlaying(false); }; window.addEventListener("wall:music-playing", h); return () => window.removeEventListener("wall:music-playing", h); }, []);
  useEffect(() => { if (!r.admin) return undefined; const id = setInterval(() => { if (r.reel.queue || r.reel.importing) r.refresh(); }, 5000); return () => clearInterval(id); }, [r.admin, r.reel.queue, r.reel.importing, r.refresh]);
  useEffect(() => {
    const onKey = (e) => {
      if (!open || !cur || e.metaKey || e.ctrlKey || e.altKey || e.repeat) return;
      const el = document.activeElement;
      if (el && (["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName) || el.isContentEditable)) return;
      if (e.key === "l" || e.key === "L") like();
      if (e.key === "k" || e.key === "K") kwiki();
    };
    window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey);
  }); // eslint-disable-line react-hooks/exhaustive-deps
  if (!r.admin || !tracks.length) return null;
  const heard = tracks.filter((t) => t.vote).length, liked = tracks.filter((t) => t.vote === "like").length;
  const audioEl = <audio ref={audio} preload="none" onTimeUpdate={() => setTime(audio.current?.currentTime || 0)} onLoadedMetadata={() => setDur(audio.current?.duration || 0)} onEnded={() => load(nextUnheard(curIndex))} onPause={() => setPlaying(false)} onPlay={() => setPlaying(true)} data-testid="reel-audio" />;
  if (krakensOpen) return audioEl; // the Krakens window sits in the same corner — the Reel steps aside (keeps playing)
  if (!open || squeeze) return (
    <>
      {audioEl}
      <button type="button" onClick={() => { if (squeeze) llama.setMinimized(true); setOpen(true); }} style={squeeze ? { bottom: "calc(var(--dock-h, 0px) + 8px)" } : undefined} className="fixed left-3 bottom-24 z-[1050] inline-flex items-center gap-2 rounded-full bg-[#120A24] text-[#F5D06F] ring-2 ring-[#F5D06F] px-3 py-2 text-xs font-black shadow-xl" data-testid="reel-open">
        <ListMusic className="h-4 w-4" /> Reel · {heard}/{tracks.length}{playing && <span className="h-2 w-2 rounded-full bg-[#F5D06F] animate-pulse" />}
      </button>
    </>
  );
  return (
    <>
    {audioEl}
    <div className="fixed left-3 bottom-24 z-[1050] w-[360px] max-w-[calc(100vw-24px)] rounded-2xl bg-[#120A24] text-white ring-2 ring-[#F5D06F] shadow-2xl" data-testid="reel-player">
      <div className="flex items-center gap-2 px-3 pt-2.5">
        <ListMusic className="h-4 w-4 text-[#F5D06F]" />
        <span className="text-[10px] font-black uppercase tracking-[0.18em] text-[#F5D06F]">The Reel · admin only</span>
        <span className="text-[10px] text-white/50">{heard}/{tracks.length} heard · {liked} ♥</span>
        <div className="ml-auto flex items-center gap-1">
          <button type="button" onClick={() => setShowQueue(!showQueue)} className="rounded-full p-1 hover:bg-white/10" title="Shows on the Reel" data-testid="reel-queue-toggle">{showQueue ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}</button>
          <button type="button" onClick={() => setOpen(false)} className="rounded-full p-1 hover:bg-white/10" aria-label="Minimize" data-testid="reel-minimize"><X className="h-4 w-4" /></button>
        </div>
      </div>
      {cur && (
        <div className="px-3 pt-2">
          <p className="m-0 text-[11px] text-white/60 truncate">{cur.artist} · {cur.show_title}</p>
          <p className="m-0 text-base font-bold leading-tight truncate" data-testid="reel-title">{cur.track_no}. {cur.title} {cur.vote && <span className={`ml-1 rounded px-1 text-[9px] font-black uppercase ${cur.vote === "like" ? "bg-[#F5D06F] text-[#120A24]" : "bg-white/10 text-white/50"}`}>{cur.vote === "like" ? "♥ liked" : "passed"}</span>}{cur.cached?.state === "on_air" && <span className="ml-1 rounded bg-[#54E3FF] text-[#120A24] px-1 text-[9px] font-black uppercase">on air</span>}</p>
          <input type="range" min={0} max={dur || cur.length_sec || 0} step={1} value={Math.min(time, dur || cur.length_sec || 0)} onChange={(e) => { if (audio.current) audio.current.currentTime = +e.target.value; }} className="mt-1 w-full accent-[#F5D06F] h-1" data-testid="reel-seek" />
          <div className="flex items-center justify-between text-[10px] text-white/50 tabular-nums"><span>{mmss(time)}</span><span>{mmss(dur || cur.length_sec)}</span></div>
        </div>
      )}
      <div className="flex items-center justify-between gap-1 px-3 pb-3 pt-1">
        <button type="button" onClick={kwiki} className="inline-flex items-center gap-1 rounded-full bg-white/10 hover:bg-red-500/40 px-3 py-2 text-xs font-black" title="Kwiki — pass and move on (K)" data-testid="reel-kwiki"><X className="h-4 w-4" /> kwiki</button>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => step(-1)} disabled={curIndex <= 0} className="rounded-full p-2 hover:bg-white/10 disabled:opacity-30" aria-label="Previous" data-testid="reel-prev"><SkipBack className="h-4 w-4" /></button>
          <button type="button" onClick={toggle} className="rounded-full bg-[#F5D06F] text-[#120A24] p-3 hover:scale-105 transition-transform" aria-label={playing ? "Pause" : "Play"} data-testid="reel-play">{playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}</button>
          <button type="button" onClick={() => step(1)} disabled={curIndex >= tracks.length - 1} className="rounded-full p-2 hover:bg-white/10 disabled:opacity-30" aria-label="Next" data-testid="reel-next"><SkipForward className="h-4 w-4" /></button>
        </div>
        <button type="button" onClick={like} className="inline-flex items-center gap-1 rounded-full bg-[#F5D06F] text-[#120A24] hover:scale-105 transition-transform px-3 py-2 text-xs font-black" title="Like — into THE VÖLT and on air (L)" data-testid="reel-like"><Heart className="h-4 w-4" /> like</button>
      </div>
      {(r.reel.queue > 0 || r.reel.importing) && <p className="m-0 px-3 pb-2 text-[10px] text-[#54E3FF] inline-flex items-center gap-1" data-testid="reel-importing"><Loader2 className="h-3 w-3 animate-spin" /> {r.reel.importing ? `bringing in ${r.reel.importing}` : "queued"}{r.reel.queue ? ` · ${r.reel.queue} more` : ""}</p>}
      {showQueue && (
        <ul className="m-0 p-0 list-none max-h-56 overflow-y-auto border-t border-white/10" data-testid="reel-queue">
          {r.reel.shows.map((s) => {
            const n = s.tracks.length, h = Object.keys(s.votes || {}).length, l = Object.values(s.votes || {}).filter((v) => v === "like").length;
            return (
              <li key={s.show_id} className={`flex items-center gap-2 px-3 py-1.5 text-xs ${cur?.show_id === s.show_id ? "bg-white/5" : ""}`} data-testid={`reel-show-${s.show_id}`}>
                <button type="button" onClick={() => load(tracks.find((t) => t.show_id === s.show_id))} className="min-w-0 flex-1 text-left hover:underline"><b className="block truncate">{s.artist}</b><span className="block truncate text-white/50">{s.date}{s.venue ? ` · ${s.venue}` : ""} · {h}/{n} heard · {l} ♥</span></button>
                <button type="button" onClick={() => r.remove(s.show_id)} className="rounded-full p-1 hover:bg-white/10" aria-label="Remove from Reel" data-testid={`reel-remove-${s.show_id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </li>
            );
          })}
          <li className="px-3 py-1.5 text-[10px] text-white/40 inline-flex items-center gap-1"><Radio className="h-3 w-3" /> ♥ = into THE VÖLT + on air · keys: L like · K kwiki</li>
        </ul>
      )}
    </div>
    </>
  );
};
